import * as React from "react";
import {Button, Dialog, DialogContent, LinearProgress} from "@mui/material";
import {Link} from "react-router-dom";
import {useRecoilValue} from "recoil";
import {ActionType} from "../../components/quickform/Model";
import {QuickForm} from "../../components/quickform/QuickForm";
import {DataColumn, DataTable} from "../../components/DataTable";
import {PubSub} from "../../components/PubSub";
import Page from "../../layout/Page";
import PageContent from "../../layout/PageContent";
import PageHeader from "../../layout/PageHeader";
import {CurrentUserIsAdmin} from "../../atoms/CurrentUser";
import {SavingUI} from "../../SavingProvider";
import {
   LogisticsPartnerListFieldsFragment,
   useLogisticsPartnersPageCreateMutation,
   useLogisticsPartnersPageQuery
} from "../../Queries";
import {createLogisticsPartnerForm, LogisticsPartnerFormData} from "./LogisticsPartnerForm";
import {LogisticsPartnerSettingsPagePath} from "./LogisticsPartnerSettingsPage";

const createForm = createLogisticsPartnerForm({
   title: "Create logisticspartner",
   cancel: true
});

const emptyPartner: LogisticsPartnerFormData = {
   name: "",
   attention: "",
   address: "",
   address2: "",
   zipCode: "",
   city: "",
   country: "",
   countryCode: "",
   mail: "",
   phone: ""
};

export const LogisticsPartnersPage = () => {
   const [creating, setCreating] = React.useState(false);
   const isAdministrator = useRecoilValue(CurrentUserIsAdmin);
   const {data, loading, refetch} = useLogisticsPartnersPageQuery();
   const [createPartner] = useLogisticsPartnersPageCreateMutation();

   if (loading || !data) {
      return <LinearProgress/>;
   }

   const columns: DataColumn<LogisticsPartnerListFieldsFragment>[] = [{
      field: "name",
      label: "Name",
      searchable: true,
      render: (name, partner) => isAdministrator
         ? <Link to={LogisticsPartnerSettingsPagePath(partner._id)}>{name}</Link>
         : name
   }, {
      field: "city",
      label: "City",
      searchable: true
   }, {
      field: "country",
      label: "Country",
      searchable: true
   }, {
      field: "mail",
      label: "Email",
      searchable: true
   }, {
      field: "phone",
      label: "Phone",
      searchable: true
   }];

   const onButton = async (action: ActionType, partner: LogisticsPartnerFormData) => {
      if (action === "cancel") {
         setCreating(false);
         return {success: true};
      }

      await SavingUI.process(async () => {
         await createPartner({
            variables: {
               name: partner.name,
               attention: partner.attention,
               address: partner.address,
               address2: partner.address2,
               zipCode: partner.zipCode,
               city: partner.city,
               country: partner.country,
               countryCode: partner.countryCode,
               mail: partner.mail,
               phone: partner.phone
            }
         });
         setCreating(false);
         await refetch();
         PubSub.emit("NavigationReload");
      }, "Logistics partner created");

      return {success: true};
   }

   return <Page>
      <PageHeader title="Logistics partners">
         {isAdministrator && <Button variant="outlined" onClick={() => setCreating(true)}>
            Create logisticspartner
         </Button>}
      </PageHeader>
      <PageContent fullHeight>
         <DataTable stateId="logisticsPartners"
                    idField="_id"
                    selections={false}
                    columns={columns}
                    rows={data.LogisticsPartners}
                    commands={[]}/>
      </PageContent>
      <Dialog open={creating} onClose={() => setCreating(false)} fullWidth maxWidth="md">
         <DialogContent>
            <QuickForm form={createForm} data={emptyPartner} onButton={onButton}/>
         </DialogContent>
      </Dialog>
   </Page>
}
